import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'components/ui/NavigationGuard';
import attendanceApi from 'services/api/attendanceApi';
import axiosInstance from 'utils/axiosInstance';
import yoloApi from 'services/api/yoloApi';

const useAttendanceRecordsLogic = () => {
  const { user } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [sortConfig, setSortConfig] = useState({ key: 'checkIn', direction: 'desc' });
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isUnknownFilter, setIsUnknownFilter] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // New state for employee counts
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [unknownEmployees, setUnknownEmployees] = useState(0);
  const [employeeSummary, setEmployeeSummary] = useState([]);
  const [summaryMonth, setSummaryMonth] = useState(new Date().getMonth() + 1);
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  // Tab state
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'employee'

  // Employee-wise state
  const [employees, setEmployees] = useState([]);
  const [timeFilter, setTimeFilter] = useState('today'); // 'today', 'week', 'month', 'year'
  const [employeeFilters, setEmployeeFilters] = useState({
    month: '',
    year: '',
    department: ''
  });

  // Employee attendance modal state
  const [isEmployeeAttendanceModalOpen, setIsEmployeeAttendanceModalOpen] = useState(false);
  const [selectedEmployeeForAttendance, setSelectedEmployeeForAttendance] = useState(null);
  const [employeeAttendanceSummary, setEmployeeAttendanceSummary] = useState(null);
  const [employeeAttendanceRecords, setEmployeeAttendanceRecords] = useState([]);

  // Notification state
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'info'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detectionMode, setDetectionMode] = useState("PERSON");

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await yoloApi.getSettings();
      // setSettings(data);

      // auto detect current mode
      const fullKitEnabled = data.find(i => i.class_name === "full-kit")?.enabled;
      setDetectionMode(fullKitEnabled ? "FULL_KIT" : "PERSON");

    } catch (err) {
      setError("Failed to load YOLO settings");
    } finally {
      setLoading(false);
    }
  };
const fetchEmployeeSummary = async (month, year) => {
  try {
    const data = await attendanceApi.getEmployeesAttendanceSummary(month, year);
    setEmployeeSummary(data);
  } catch (err) {
    console.error("Error fetching employee summary:", err);
    showNotification("Failed to fetch employee summary", "error");
  }
};
  // Function to show notification
  const showNotification = (message, type = 'info') => {
    setNotification({
      show: true,
      message,
      type
    });
  };

  // Function to hide notification
  const hideNotification = () => {
    setNotification({
      show: false,
      message: '',
      type: 'info'
    });
  };

  // Fetch employees for dropdown
  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get("/employee/getall");
      setEmployees(response.data.data.employees.filter((r) => r.isUnkown == false) || []);
    } catch (err) {
      console.error("Error fetching employees:", err);
      showNotification("Failed to fetch employees", "error");
    }
  };

  // Fetch attendance data
  const fetchAttendance = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get("attendance/getall");
      const data = response?.data || {};
      console.log("data,,", data)

      // Convert checkIn/checkOut strings → Date objects and parse PPE items
      const records = data?.records
        .filter((r) => r.employee.isUnkown == false)
        ?.map((r) => ({
          ...r,
          checkIn: r.checkIn ? new Date(r.checkIn) : null,
          checkOut: r.checkOut ? new Date(r.checkOut) : null,
          // Parse PPE items if they're strings
          missingPpeItems: typeof r.missingPpeItems === 'string'
            ? JSON.parse(r.missingPpeItems)
            : r.missingPpeItems || [],
          detectedPpeItems: typeof r.detectedPpeItems === 'string'
            ? JSON.parse(r.detectedPpeItems)
            : r.detectedPpeItems || []
        })) || [];

      setSummary(data?.summary || {});
      setAttendanceRecords(records);
      setFilteredRecords(records);

      // Calculate employee counts
      calculateEmployeeCounts(records);
    } catch (err) {
      console.error("Error fetching attendance:", err);
      showNotification("Failed to fetch attendance records", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch employee attendance summary
  const fetchEmployeeAttendanceSummary = async (employeeId) => {
    try {
      const response = await axiosInstance.get(`/employee/get-summary/${employeeId}`);
      setEmployeeAttendanceSummary(response.data);
    } catch (err) {
      console.error("Error fetching employee attendance summary:", err);
      showNotification("Failed to fetch employee attendance summary", "error");
    }
  };

  // Fetch employee attendance records
  const fetchEmployeeAttendanceRecords = async (employeeId, filter) => {
    try {
      const response = await axiosInstance.get(
        `/employee/getbyid/${employeeId}?filter=${filter}`
      );
      const records = response?.data?.records?.map((r) => ({
        ...r,
        checkIn: r.checkIn ? new Date(r.checkIn) : null,
        checkOut: r.checkOut ? new Date(r.checkOut) : null,
      })) || [];
      setEmployeeAttendanceRecords(records);
    } catch (err) {
      console.error("Error fetching employee attendance records:", err);
      showNotification("Failed to fetch employee attendance records", "error");
    }
  };

  // Fetch employee attendance master data
  const fetchEmployeeAttendanceMaster = async (employeeId) => {
    try {
      if (!user?.company_id) return null;
      const data = await attendanceApi.getAttendanceMasterByEmployee(
        employeeId,
        user.company_id
      );
      return data;
    } catch (err) {
      console.error("Error fetching employee attendance master:", err);
      showNotification("Failed to fetch employee attendance master", "error");
      return null;
    }
  };

  // Fetch employee attendance transaction data
  const fetchEmployeeAttendanceTransaction = async (employeeId, month, year) => {
    try {
      if (!user?.company_id) return null;
      const data = await attendanceApi.getAttendanceTransaction(
        employeeId,
        month,
        year,
        user.company_id
      );
      return data;
    } catch (err) {
      console.error("Error fetching employee attendance transaction:", err);
      showNotification("Failed to fetch employee attendance transaction", "error");
      return null;
    }
  };

  useEffect(() => {
  fetchEmployees();
  fetchAttendance();
  fetchSettings();
  fetchEmployeeSummary(summaryMonth, summaryYear);
}, [summaryMonth, summaryYear]);
const handleSummaryMonthChange = (month) => {
  setSummaryMonth(month);
};

const handleSummaryYearChange = (year) => {
  setSummaryYear(year);
};
  // Function to calculate employee counts
  const calculateEmployeeCounts = (records) => {
    // Get unique employee IDs
    const uniqueEmployeeIds = new Set();
    const unknownEmployeeIds = new Set();

    records.forEach(record => {
      if (record.employee && record.employee.id) {
        uniqueEmployeeIds.add(record.employee.id);

        // Check if employee is unknown
        if (record.employee.isUnkown) {
          unknownEmployeeIds.add(record.employee.id);
        }
      }
    });

    setTotalEmployees(uniqueEmployeeIds.size);
    setUnknownEmployees(unknownEmployeeIds.size);
  };

  // Admin stats
  const getStats = () => ({
    totalPresent: summary?.total_present,
    totalLate: summary?.late_arrivals,
    totalAbsent: summary?.absent,
    totalOvertime: summary?.overtime,
    totalRecords: summary?.total_records,
    attendancePercentage: summary?.attendance_percentage,
    totalEmployees: totalEmployees,
    unknownEmployees: unknownEmployees
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filters
  const handleFiltersChange = (filters) => {
    let filtered = [...attendanceRecords];

    if (filters?.employeeSearch) {
      filtered = filtered?.filter(record =>
        record?.employee?.name?.toLowerCase()?.includes(filters?.employeeSearch?.toLowerCase()) ||
        record?.employee?.id?.toLowerCase()?.includes(filters?.employeeSearch?.toLowerCase())
      );
    }

    if (filters?.dateFrom) {
      filtered = filtered?.filter(record =>
        record?.checkIn && new Date(record.checkIn) >= new Date(filters.dateFrom)
      );
    }

    if (filters?.dateTo) {
      filtered = filtered?.filter(record =>
        record?.checkIn && new Date(record.checkIn) <= new Date(filters.dateTo + 'T23:59:59')
      );
    }

    if (filters?.status) {
      filtered = filtered?.filter(record => record?.status === filters?.status);
    }

    if (filters?.cameraLocation) {
      filtered = filtered?.filter(record => record?.cameraLocation === filters?.cameraLocation);
    }
    if (filters?.isUnknown !== undefined && filters?.isUnknown !== '') {
      filtered = filtered?.filter(record =>
        filters?.isUnknown === 'unknown' ? record?.employee?.isUnkown : !record?.employee?.isUnkown
      );
    }
    setFilteredRecords(filtered);
    setCurrentPage(1);
  };

  // Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredRecords]?.sort((a, b) => {
      let aValue = a?.[key];
      let bValue = b?.[key];

      if (key === 'employee') {
        aValue = a?.employee?.name;
        bValue = b?.employee?.name;
      }

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredRecords(sorted);
    setSortConfig({ key, direction });
  };

  // Selection handling
  const handleSelectRecord = (recordId, isSelected) => {
    if (isSelected) {
      setSelectedRecords([...selectedRecords, recordId]);
    } else {
      setSelectedRecords(selectedRecords?.filter(id => id !== recordId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const currentPageRecords = getCurrentPageRecords()?.map(record => record?.id);
      setSelectedRecords([...new Set([...selectedRecords, ...currentPageRecords])]);
    } else {
      const currentPageRecords = getCurrentPageRecords()?.map(record => record?.id);
      setSelectedRecords(selectedRecords?.filter(id => !currentPageRecords?.includes(id)));
    }
  };

  // Bulk actions
  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} on records:`, selectedRecords);
    showNotification(`${action} action applied to ${selectedRecords.length} records`, "success");
    setSelectedRecords([]);
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const response = await axiosInstance.get("employee/get/export/csv", {
        responseType: "blob",
      });

      // Create a download link for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `employees_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification("Employee data exported successfully", "success");
    } catch (err) {
      console.error("Export failed:", err);
      showNotification("Failed to export employee data", "error");
    }
  };

  // Pagination
  const getCurrentPageRecords = () => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredRecords?.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredRecords?.length / recordsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRecordsPerPageChange = (newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  };

  const handleToggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleUpdateRecord = async (updatedData) => {
    try {
      const response = await axiosInstance.put(
        `attendance/update/${editingRecord.id}`,
        updatedData
      );

      // Update the record in the state
      const updatedRecords = attendanceRecords.map(record =>
        record.id === editingRecord.id ? response.data : record
      );
      setAttendanceRecords(updatedRecords);

      // Update filtered records as well
      const updatedFilteredRecords = filteredRecords.map(record =>
        record.id === editingRecord.id ? response.data : record
      );
      setFilteredRecords(updatedFilteredRecords);

      // Recalculate employee counts
      calculateEmployeeCounts(updatedRecords);

      setIsEditModalOpen(false);
      setEditingRecord(null);

      showNotification("Attendance record updated successfully", "success");
    } catch (err) {
      console.error("Error updating attendance record:", err);
      showNotification("Failed to update attendance record", "error");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await axiosInstance.delete(`attendance/delete/${recordToDelete.id}`);

      // Remove the record from the state
      const updatedRecords = attendanceRecords.filter(record => record.id !== recordToDelete.id);
      setAttendanceRecords(updatedRecords);

      // Remove from filtered records as well
      const updatedFilteredRecords = filteredRecords.filter(record => record.id !== recordToDelete.id);
      setFilteredRecords(updatedFilteredRecords);

      // Recalculate employee counts
      calculateEmployeeCounts(updatedRecords);

      setIsDeleteModalOpen(false);
      setRecordToDelete(null);

      showNotification("Attendance record deleted successfully", "success");
    } catch (err) {
      console.error("Error deleting attendance record:", err);
      showNotification("Failed to delete attendance record", "error");
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedRecords([]);
    setCurrentPage(1);
  };

  // Handle time filter change
  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
  };

  // Handle view employee attendance
  const handleViewEmployeeAttendance = async (employee) => {
    setSelectedEmployeeForAttendance(employee);
    await fetchEmployeeAttendanceSummary(employee.id);
    await fetchEmployeeAttendanceRecords(employee.id, timeFilter);
    setIsEmployeeAttendanceModalOpen(true);
  };

  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [recordToSwitch, setRecordToSwitch] = useState(null);

  // Add these functions in the AttendanceRecords component
  const handleSwitchClick = (record) => {
    setRecordToSwitch(record);
    setIsSwitchModalOpen(true);
  };

  const handleCloseSwitchModal = () => {
    setIsSwitchModalOpen(false);
    setRecordToSwitch(null);
  };

  // Handle employee filter changes
  const handleEmployeeFilterChange = (filters) => {
    setEmployeeFilters(filters);

    // Re-fetch employees if needed
    if (filters.month || filters.year || filters.department) {
      fetchEmployees();
    }
  };

  return {
    // State
    attendanceRecords,
    summary,
    filteredRecords,
    selectedRecords,
    currentPage,
    recordsPerPage,
    sortConfig,
    isLoading,
    isMobile,
    sidebarCollapsed,
    isUnknownFilter,
    editingRecord,
    isEditModalOpen,
    recordToDelete,
    isDeleteModalOpen,
    totalEmployees,
    unknownEmployees,
    activeTab,
    employees,
    timeFilter,
    isEmployeeAttendanceModalOpen,
    selectedEmployeeForAttendance,
    employeeAttendanceSummary,
    employeeAttendanceRecords,
    isSwitchModalOpen,
    recordToSwitch,
    employeeFilters,

    // State setters
    setAttendanceRecords,
    setSummary,
    setFilteredRecords,
    setSelectedRecords,
    setCurrentPage,
    setRecordsPerPage,
    setSortConfig,
    setIsLoading,
    setIsMobile,
    setSidebarCollapsed,
    setIsUnknownFilter,
    setEditingRecord,
    setIsEditModalOpen,
    setRecordToDelete,
    setIsDeleteModalOpen,
    setTotalEmployees,
    setUnknownEmployees,
    setActiveTab,
    setEmployees,
    setTimeFilter,
    setIsEmployeeAttendanceModalOpen,
    setSelectedEmployeeForAttendance,
    setEmployeeAttendanceSummary,
    setEmployeeAttendanceRecords,
    setIsSwitchModalOpen,
    setRecordToSwitch,
    setEmployeeFilters,

    // Functions
    fetchEmployees,
    fetchAttendance,
    fetchEmployeeAttendanceSummary,
    fetchEmployeeAttendanceRecords,
    fetchEmployeeAttendanceMaster,
    fetchEmployeeAttendanceTransaction,
    calculateEmployeeCounts,
    getStats,
    getCurrentPageRecords,
    handleFiltersChange,
    handleSort,
    handleSelectRecord,
    handleSelectAll,
    handleBulkAction,
    handleExport,
    handlePageChange,
    handleRecordsPerPageChange,
    handleToggleCollapse,
    handleEditClick,
    handleDeleteClick,
    handleUpdateRecord,
    handleConfirmDelete,
    handleTabChange,
    handleTimeFilterChange,
    handleViewEmployeeAttendance,
    handleSwitchClick,
    handleCloseSwitchModal,
    user,
    totalPages,
    detectionMode,
    handleEmployeeFilterChange,
    employeeSummary,
  summaryMonth,
  summaryYear,
  fetchEmployeeSummary,
  handleSummaryMonthChange,
  handleSummaryYearChange,
  };
};

export default useAttendanceRecordsLogic;